import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { type WebpageFormikValue } from '../shared';
import { colSpan } from './ColumnListBlock';
import ReorderingButtons, { getNewBlock } from './shared';

type CustomBlockProps = {
    name: string;
    index: number;
    disabled?: boolean;
};

const CustomBlock = ({ name, index, disabled = false }: CustomBlockProps) => {
    const { t } = useTranslation(['webpageDetails']);
    const { values, setFieldValue } = useFormikContext<WebpageFormikValue>();

    const { webpageBlockType } = useSystemOptions();

    const markdownInfoTooltip = useMarkDownInfoTooltip();

    const onSelect = useCallback(
        value => {
            values.webPageBlock[index] = getNewBlock(value);
            setFieldValue(name, getNewBlock(value));
        },
        [index, name, setFieldValue, values.webPageBlock]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:fields.custom.blockName')}>
                <Panel
                    key={t('webpageDetails:fields.custom.blockName')}
                    extra={
                        !disabled && <ReorderingButtons index={index} setFieldValue={setFieldValue} values={values} />
                    }
                    header={t('webpageDetails:fields.custom.blockName')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.blockType', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.type`}
                                onSelect={onSelect}
                                options={webpageBlockType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.custom.title', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.title`}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('webpageDetails:fields.custom.markdownString', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.markdownString`}
                                tooltip={markdownInfoTooltip}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default CustomBlock;
